import React, { useMemo, useRef, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CobuContainer, CobuStack, CobuText, CobuHeader, CobuBackButton } from '@cobu/ui'
import { Strings } from './constants/Strings'
import { showErrorMessage } from './utils/messageToast'

type WebViewState = {
  url?: string
  title?: string
  injectedJavaScript?: string
  returnTo?: string
  returnToTab?: string
}

export default function WebViewPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const strings = Strings.get().webView
  const [isLoading, setIsLoading] = useState(true)
  const hasShownErrorRef = useRef(false)

  const { url, title, returnTo, returnToTab } = (location.state as WebViewState) || {}

  const resolvedUrl = useMemo(() => {
    if (!url) return ''
    try {
      // If already absolute, this succeeds
      // eslint-disable-next-line no-new
      new URL(url)
      return url
    } catch {
      const path = url.startsWith('/') ? url : `/${url}`
      return `${window.location.origin}${path}`
    }
  }, [url])

  useEffect(() => {
    if (hasShownErrorRef.current) return
    if (!resolvedUrl) {
      showErrorMessage(strings.errors.noUrl)
      hasShownErrorRef.current = true
    }
  }, [resolvedUrl, strings.errors.noUrl])

  if (!resolvedUrl) {
    return (
      <CobuContainer f={1} bg="$white" px="$4" jc="center" ai="center">
        <CobuText variant="heading" fontSize="$5" fontWeight="bold" color="$cobuDarkBlue" pb="$2">
          {strings.errors.noUrl}
        </CobuText>
      </CobuContainer>
    )
  }

  return (
    <CobuContainer f={1} bg="$white" h="100dvh">
      <CobuHeader
        title={title || strings.defaultTitle}
        renderLeft={() => (
          <CobuBackButton
            onPress={() => {
              if (returnTo) {
                // Navigate to specific route with optional tab parameter
                const targetPath = returnToTab ? `${returnTo}?tab=${returnToTab}` : returnTo
                navigate(targetPath)
              } else {
                navigate(-1)
              }
            }}
          />
        )}
      />

      <CobuStack f={1}>
        <iframe
          title={title || strings.defaultTitle}
          src={resolvedUrl}
          style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
          allow="clipboard-write; fullscreen;"
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation-by-user-activation"
          onLoad={() => setIsLoading(false)}
        />
        {isLoading && (
          <CobuStack jc="center" ai="center" bg="$white" pos="absolute" t={0} l={0} r={0} b={0} z={10}>
            <CobuText variant="body" color="$cobuDarkBlue" mt="$3">
              {Strings.get().common.loading}
            </CobuText>
          </CobuStack>
        )}
      </CobuStack>
    </CobuContainer>
  )
}